<script lang="ts">
	import { Modal, Button, Text } from '@Shelly/shelly-design-system';
	import type { FlowEdge, FlowNode } from '../types/flow-types';
	import { getEffectiveNodeDisplayLabel } from '../PropertyEditors/helpers/LabelGenerator';

	export let showModal = false;
	export let nodes: FlowNode[] = [];
	export let edges: FlowEdge[] = [];
	export let onClose: () => void;
	export let onFocusNode: (nodeId: string) => void;

	$: outline = buildOutline(nodes, edges);

	function buildOutline(allNodes: FlowNode[], allEdges: FlowEdge[]) {
		const semantic = allNodes.filter((node) => node.type !== 'note');
		const incoming = new Map(semantic.map((node) => [node.id, 0]));
		const outgoing = new Map(semantic.map((node) => [node.id, [] as FlowEdge[]]));
		for (const edge of allEdges) {
			if (!incoming.has(edge.target) || !outgoing.has(edge.source)) continue;
			incoming.set(edge.target, (incoming.get(edge.target) ?? 0) + 1);
			outgoing.get(edge.source)?.push(edge);
		}
		const roots = semantic.filter((node) => (incoming.get(node.id) ?? 0) === 0);
		const rows: { node: FlowNode; depth: number; branch: string; reference: boolean }[] = [];
		const visited = new Set<string>();
		function visit(node: FlowNode, depth: number, branch = '') {
			if (visited.has(node.id)) {
				rows.push({ node, depth, branch, reference: true });
				return;
			}
			visited.add(node.id);
			rows.push({ node, depth, branch, reference: false });
			for (const edge of outgoing.get(node.id) ?? []) {
				const next = semantic.find((candidate) => candidate.id === edge.target);
				if (next) visit(next, Math.min(depth + 1, 6), humanBranch(edge.sourceHandle));
			}
		}
		for (const root of roots) visit(root, 0);
		for (const node of semantic) if (!visited.has(node.id)) visit(node, 0, 'cycle or detached');
		return rows;
	}

	function humanBranch(handle: string | undefined) {
		if (handle === 'true') return 'When matched';
		if (handle === 'false') return 'Otherwise';
		if (handle === 'default') return 'Default case';
		if (handle?.startsWith('case-')) return 'Matching case';
		return 'Next';
	}
</script>

<Modal
	{showModal}
	title="Flow outline · Experimental"
	type="fullScreen"
	backdropClose={false}
	on:close={onClose}
>
	<svelte:fragment slot="content">
		<div class="outline-content">
			<div class="experimental-note">
				<i class="fas fa-flask" aria-hidden="true"></i><span
					>This read-only outline makes large graphs easier to inspect on narrow screens. The
					canvas remains the source of truth.</span
				>
			</div>
			{#if outline.length}
				<div class="outline-list" role="list">
					{#each outline as row, index (`${row.node.id}-${index}`)}
						<button
							type="button"
							role="listitem"
							class:reference={row.reference}
							class="outline-row"
							style="--depth: {row.depth}"
							on:click={() => onFocusNode(row.node.id)}
						>
							<span class="branch"
								>{row.branch || (row.depth === 0 ? 'Starts here' : 'Next')}</span
							>
							<span class="node-line"
								><i class="fas {row.node.data.icon || 'fa-circle'}" aria-hidden="true"
								></i><strong>{getEffectiveNodeDisplayLabel(row.node)}</strong></span
							>
							<span class="node-meta"
								>{row.reference
									? 'Already shown · opens original'
									: row.node.validated === false
										? row.node.errors?.[0] || 'Needs attention'
										: row.node.type.replace(/([A-Z])/g, ' $1')}</span
							>
						</button>
					{/each}
				</div>
			{:else}<Text variant="secondary" size="m">Add nodes to see a readable outline.</Text>{/if}
		</div>
	</svelte:fragment>
	<div slot="footer" class="modal-footer">
		<Button variant="primary" on:click={onClose}>Done</Button>
	</div>
</Modal>

<style>
	.outline-content {
		width: min(760px, 100%);
		margin: 0 auto;
		display: grid;
		gap: 14px;
	}
	.experimental-note {
		display: flex;
		gap: 10px;
		padding: 11px 12px;
		border-radius: 9px;
		color: #e9d5ff;
		background: rgba(168, 85, 247, 0.1);
		border: 1px solid rgba(192, 132, 252, 0.28);
		font-size: 12px;
		line-height: 1.4;
	}
	.outline-list {
		display: grid;
		gap: 7px;
	}
	.outline-row {
		margin-left: calc(var(--depth) * min(28px, 5vw));
		display: grid;
		gap: 4px;
		padding: 10px 12px;
		border-radius: 9px;
		border: 1px solid rgba(255, 255, 255, 0.1);
		background: rgba(255, 255, 255, 0.035);
		color: #f4f4f5;
		text-align: left;
		cursor: pointer;
	}
	.outline-row:hover {
		border-color: rgba(96, 165, 250, 0.55);
	}
	.outline-row.reference {
		border-style: dashed;
		opacity: 0.78;
	}
	.branch {
		color: #93c5fd;
		font-size: 10px;
		text-transform: uppercase;
		letter-spacing: 0.04em;
	}
	.node-line {
		display: flex;
		gap: 8px;
		align-items: center;
	}
	.node-line i {
		color: #60a5fa;
		width: 16px;
		text-align: center;
	}
	.node-meta {
		padding-left: 24px;
		color: #a1a1aa;
		font-size: 11px;
		text-transform: capitalize;
	}
	.modal-footer {
		display: flex;
		justify-content: flex-end;
	}
</style>
